import { EditableElement, ShapeType, CreateElementOptions, AnchorPoint, ConnectorElement } from './types';
import { COLORS, SIZES, SPACING, SHADOWS, FONTS } from '../utils/constants';
import { calculateCurvedPath } from '../utils/connectorPathUtils';
import { findNearestAnchorOnShape } from '../utils/connectorUtils';

/**
 * ElementFactory provides factory methods for creating different types of elements
 */

let elementCounter = 0;

const generateId = (type: string): string => {
  elementCounter++;
  return `${type}-${Date.now()}-${elementCounter}`;
};

// Color palettes for different element types
const STICKY_COLORS = ['#FFF9C4', '#FFCCBC', '#B2DFDB', '#D1C4E9', '#F8BBD0', '#C5E1A5'];
const SHAPE_COLORS = ['#2196F3', '#4CAF50', '#FF9800', '#9C27B0', '#F44336', '#00BCD4'];

const getRandomColor = (colors: string[]): string => {
  return colors[Math.floor(Math.random() * colors.length)];
};

/**
 * Create a sticky note element
 */
export const createStickyNote = (options: CreateElementOptions): EditableElement => {
  const width = Math.max(options.width || SIZES.STICKY_NOTE_WIDTH, SIZES.STICKY_NOTE_WIDTH * 0.5);
  const height = Math.max(options.height || SIZES.STICKY_NOTE_MIN_HEIGHT, SIZES.STICKY_NOTE_MIN_HEIGHT * 0.4);
  
  return {
    id: generateId('sticky'),
    type: 'sticky',
    x: options.x,
    y: options.y,
    width,
    height,
    rotation: 0,
    data: {
      text: options.data?.text || 'Double-click to edit',
      backgroundColor: options.data?.backgroundColor || COLORS.YELLOW,
      fontSize: 14,
      fontFamily: FONTS.FAMILY,
      fill: COLORS.BLACK,
      align: 'left',
      padding: SPACING.STICKY_PADDING,
      shadowColor: SHADOWS.STICKY_NOTE.color,
      shadowBlur: SHADOWS.STICKY_NOTE.blur,
      shadowOffsetX: SHADOWS.STICKY_NOTE.offset.x,
      shadowOffsetY: SHADOWS.STICKY_NOTE.offset.y,
      shadowOpacity: SHADOWS.STICKY_NOTE.opacity,
      cornerRadius: SIZES.STICKY_NOTE_CORNER_RADIUS,
      ...(options.data || {}),
    },
  };
};

/**
 * Create an organizer element (orange section wrapper with title)
 */
export const createOrganizer = (options: CreateElementOptions): EditableElement => {
  const width = Math.max(options.width || 600, 300);
  const height = Math.max(options.height || 400, 200);
  
  return {
    id: generateId('organizer'),
    type: 'organizer',
    x: options.x,
    y: options.y,
    width,
    height,
    rotation: 0,
    data: {
      title: options.data?.title || 'Section Title',
      ...(options.data || {}),
    },
  };
};

/**
 * Create a section element (white section box)
 */
export const createSection = (options: CreateElementOptions): EditableElement => {
  const width = Math.max(options.width || 400, 200);
  const height = Math.max(options.height || 300, 150);
  
  return {
    id: generateId('section'),
    type: 'section',
    x: options.x,
    y: options.y,
    width,
    height,
    rotation: 0,
    data: {
      title: options.data?.title || '',
      padding: options.data?.padding || 40,
      ...(options.data || {}),
    },
  };
};

/**
 * Create a text element
 */
export const createText = (options: CreateElementOptions): EditableElement => {
  const width = options.width || 200;
  const height = options.height || 60;
  
  return {
    id: generateId('text'),
    type: 'text',
    x: options.x,
    y: options.y,
    width,
    height,
    rotation: 0,
    data: {
      text: options.data?.text || 'Double-click to edit',
      fontSize: options.data?.fontSize || 16,
      fontFamily: options.data?.fontFamily || 'Arial',
      fontStyle: 'normal',
      fill: options.data?.fill || '#000000',
      align: 'left',
      ...(options.data || {}),
    },
  };
};

/**
 * Create an image element
 */
export const createImage = (
  options: CreateElementOptions & { 
    data: { src: string; alt?: string; preserveAspectRatio?: boolean }
  }
): EditableElement => {
  // Use 0x0 dimensions initially - renderer will use natural image dimensions when loaded
  // This ensures images appear at their actual size rather than a small default size
  const width = options.width || 0;
  const height = options.height || 0;
  
  return {
    id: generateId('image'),
    type: 'image',
    x: options.x,
    y: options.y,
    width,
    height,
    rotation: 0,
    data: {
      src: options.data.src,
      alt: options.data.alt,
      preserveAspectRatio: options.data.preserveAspectRatio !== undefined 
        ? options.data.preserveAspectRatio 
        : true,
      ...(options.data || {}),
    },
  };
};

/**
 * Create a shape element (rectangle, circle, triangle, etc.)
 */
export const createShape = (
  shapeType: ShapeType,
  options: CreateElementOptions
): EditableElement => {
  // Set default dimensions based on shape type
  let width = options.width || 150;
  let height = options.height || 150;
  
  // For pill rectangle, ensure width > height for horizontal orientation
  if (shapeType === 'rectangle-pill') {
    if (!options.width && !options.height) {
      width = 200;
      height = 60;
    } else if (width < height) {
      // Swap if height is larger
      [width, height] = [height, width];
    }
  }
  
  // Line types use gray stroke, others use white fill with dark gray stroke
  const isLineType = shapeType === 'line-curved-arrow' || shapeType === 'line-parallel';
  const defaultFill = isLineType ? 'transparent' : '#FFFFFF';
  const defaultStroke = isLineType ? '#9E9E9E' : '#6B7280'; // Dark gray for shapes
  
  // Default corner radius for rounded shapes (12px)
  const defaultCornerRadius = 
    shapeType === 'rectangle-rounded' || 
    shapeType === 'triangle-up' || 
    shapeType === 'triangle-down' || 
    shapeType === 'diamond'
      ? 12 
      : shapeType === 'rectangle-pill'
      ? Math.min(width, height) / 2  // Fully rounded for pill
      : undefined;
  
  return {
    id: generateId(`shape-${shapeType}`),
    type: 'shape',
    x: options.x,
    y: options.y,
    width,
    height,
    rotation: 0,
    data: {
      shapeType,
      text: options.data?.text || 'Add text',
      fill: options.data?.fill || defaultFill,
      stroke: options.data?.stroke || defaultStroke,
      strokeWidth: options.data?.strokeWidth !== undefined ? options.data.strokeWidth : 4, // Default 4px stroke
      opacity: options.data?.opacity || 1,
      backgroundColor: options.data?.backgroundColor || defaultFill,
      textFill: options.data?.textFill || '#000000',
      align: options.data?.align || 'center', // Default center alignment for shapes
      cornerRadius: options.data?.cornerRadius !== undefined ? options.data.cornerRadius : defaultCornerRadius,
      ...(options.data || {}),
    },
  };
};

/**
 * Create a connector element (Figma-style)
 */
export const createConnector = (
  startAnchor: AnchorPoint,
  endAnchor: AnchorPoint,
  options: {
    pathType?: 'straight' | 'curved' | 'bent';
    startElementId?: string | null;
    endElementId?: string | null;
    stroke?: string;
    strokeWidth?: 'small' | 'medium' | 'large' | 'xlarge';
    dashEnabled?: boolean;
    arrowStart?: 'none' | 'solid' | 'line' | 'triangle' | 'diamond';
    arrowEnd?: 'none' | 'solid' | 'line' | 'triangle' | 'diamond';
  } = {}
): ConnectorElement => {
  // Calculate bounding box
  const minX = Math.min(startAnchor.x, endAnchor.x);
  const minY = Math.min(startAnchor.y, endAnchor.y);
  const maxX = Math.max(startAnchor.x, endAnchor.x);
  const maxY = Math.max(startAnchor.y, endAnchor.y);
  
  return {
    id: generateId('connector'),
    type: 'connector',
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
    rotation: 0,
    data: {
      pathType: options.pathType || 'curved',
      startElementId: options.startElementId || null,
      endElementId: options.endElementId || null,
      startAnchor,
      endAnchor,
      stroke: options.stroke || '#6B7280',
      strokeWidth: options.strokeWidth || 'medium',
      dashEnabled: options.dashEnabled || false,
      arrowStart: options.arrowStart || 'none',
      arrowEnd: options.arrowEnd || 'solid',
      textPosition: 0.5,
      textBackgroundEnabled: false,
      // Store control points for curved paths
      ...(options.pathType === 'curved' ? {
        controlPoints: calculateCurvedPath(startAnchor, endAnchor).controlPoints,
      } : {}),
    },
  };
};

/**
 * Calculate element bounds from drag start/end points
 */
export const calculateBounds = (
  startX: number,
  startY: number,
  endX: number,
  endY: number
): { x: number; y: number; width: number; height: number } => {
  const x = Math.min(startX, endX);
  const y = Math.min(startY, endY);
  const width = Math.abs(endX - startX);
  const height = Math.abs(endY - startY);
  
  return { x, y, width, height };
};

/**
 * Create element based on bounds (drag operation)
 */
export const createElementFromBounds = (
  mode: { type: string; shapeType?: ShapeType },
  startX: number,
  startY: number,
  endX: number,
  endY: number,
  minSize: number = 20
): EditableElement | null => {
  const bounds = calculateBounds(startX, startY, endX, endY);
  
  // Enforce minimum size
  if (bounds.width < minSize || bounds.height < minSize) {
    return null;
  }
  
  const options: CreateElementOptions = {
    x: bounds.x,
    y: bounds.y,
    width: bounds.width,
    height: bounds.height,
  };
  
  switch (mode.type) {
    case 'sticky':
      return createStickyNote(options);
    case 'organizer':
      return createOrganizer(options);
    case 'section':
      return createSection(options);
    case 'text':
      return createText(options);
    case 'shape':
      if (mode.shapeType) {
        return createShape(mode.shapeType, options);
      }
      return null;
    case 'connector':
      // Connectors are created via drag-to-connect, not via bounds
      // Return null for legacy compatibility
      return null;
    default:
      return null;
  }
};

/**
 * Create element with default size (click operation)
 */
export const createElementAtPoint = (
  mode: { type: string; shapeType?: ShapeType },
  x: number,
  y: number
): EditableElement | null => {
  const options: CreateElementOptions = { x, y };
  
  switch (mode.type) {
    case 'sticky':
      return createStickyNote(options);
    case 'organizer':
      return createOrganizer(options);
    case 'section':
      return createSection(options);
    case 'text':
      return createText(options);
    case 'shape':
      if (mode.shapeType) {
        return createShape(mode.shapeType, options);
      }
      return null;
    case 'connector':
      // Connectors are created via drag-to-connect, not via point click
      // Return null for legacy compatibility
      return null;
    default:
      return null;
  }
};

/**
 * Create a new shape from an anchor point, duplicating the source element's style
 * Returns the new shape and the target anchor point for connecting
 * @param useDefaultText - If true, create elements with default/placeholder text instead of copying source text
 */
export function createShapeFromAnchor(
  sourceElement: EditableElement,
  sourceAnchor: AnchorPoint,
  offset: number = 100,
  useDefaultText: boolean = false
): { shape: EditableElement; targetAnchor: AnchorPoint } {
  // Calculate position based on anchor side
  let newX: number;
  let newY: number;
  
  switch (sourceAnchor.side) {
    case 'right':
      newX = sourceAnchor.x + offset;
      newY = sourceAnchor.y - sourceElement.height / 2;
      break;
    case 'left':
      newX = sourceAnchor.x - offset - sourceElement.width;
      newY = sourceAnchor.y - sourceElement.height / 2;
      break;
    case 'bottom':
      newX = sourceAnchor.x - sourceElement.width / 2;
      newY = sourceAnchor.y + offset;
      break;
    case 'top':
      newX = sourceAnchor.x - sourceElement.width / 2;
      newY = sourceAnchor.y - offset - sourceElement.height;
      break;
    default:
      // Default to right side
      newX = sourceAnchor.x + offset;
      newY = sourceAnchor.y - sourceElement.height / 2;
  }

  // Prepare options - use default text if requested
  let options: CreateElementOptions;
  if (useDefaultText) {
    // Create with default text (don't copy source text)
    options = {
      x: newX,
      y: newY,
      width: sourceElement.width,
      height: sourceElement.height,
      data: {},
    };
    // Copy style properties but not text content
    if (sourceElement.data) {
      const { text, title, ...styleData } = sourceElement.data;
      options.data = { ...styleData };
    }
  } else {
    // Duplicate source element style including text
    options = {
      x: newX,
      y: newY,
      width: sourceElement.width,
      height: sourceElement.height,
      data: { ...sourceElement.data },
    };
  }

  // Create new shape based on source type
  let newShape: EditableElement;
  switch (sourceElement.type) {
    case 'sticky':
      // Default text is 'Double-click to edit' (handled in createStickyNote)
      newShape = createStickyNote(options);
      break;
    case 'text':
      // Default text is 'Double-click to edit' (handled in createText)
      newShape = createText(options);
      break;
    case 'shape':
      const shapeType = sourceElement.data?.shapeType as ShapeType | undefined;
      if (shapeType) {
        // For shapes, always duplicate style/data (including text if present)
        newShape = createShape(shapeType, options);
      } else {
        // Fallback to rounded rectangle
        newShape = createShape('rectangle-rounded', options);
      }
      break;
    case 'organizer':
      // Default title is 'Section Title' (handled in createOrganizer)
      newShape = createOrganizer(options);
      break;
    case 'section':
      // Default title is '' (empty string, handled in createSection)
      newShape = createSection(options);
      break;
    default:
      // Fallback to sticky note
      newShape = createStickyNote(options);
  }

  // Calculate target anchor (opposite side of source anchor)
  const oppositeSide: 'top' | 'right' | 'bottom' | 'left' = 
    sourceAnchor.side === 'right' ? 'left' :
    sourceAnchor.side === 'left' ? 'right' :
    sourceAnchor.side === 'top' ? 'bottom' : 'top';

  const targetAnchor = findNearestAnchorOnShape(
    { x: sourceAnchor.x, y: sourceAnchor.y },
    newShape
  );
  
  // Override side to ensure it's on the opposite side
  targetAnchor.side = oppositeSide;
  
  // Recalculate position based on opposite side
  switch (oppositeSide) {
    case 'left':
      targetAnchor.x = newShape.x;
      targetAnchor.y = newShape.y + newShape.height / 2;
      break;
    case 'right':
      targetAnchor.x = newShape.x + newShape.width;
      targetAnchor.y = newShape.y + newShape.height / 2;
      break;
    case 'top':
      targetAnchor.x = newShape.x + newShape.width / 2;
      targetAnchor.y = newShape.y;
      break;
    case 'bottom':
      targetAnchor.x = newShape.x + newShape.width / 2;
      targetAnchor.y = newShape.y + newShape.height;
      break;
  }

  return { shape: newShape, targetAnchor };
}
